import { ClientNotificationService, EClientNotificationsEventTypes, INotification } from '@shared/push-notification';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { tapResponse } from '@ngrx/operators';

export interface IClientNotificationStore {
  unreadNotificationsCount: number;
  notifications: {
    eventData: any;
    eventName: string;
  };
  signalREvent: any;
  notificationsMenu: {
    opened: boolean;
    notifications: INotification[];
    currentPage: number;
    hasMoreItems: boolean;
  };
}

export const clientNotificationInitialStore: IClientNotificationStore = {
  unreadNotificationsCount: 0,
  notifications: null,
  signalREvent: null,
  notificationsMenu: {
    opened: false,
    notifications: [],
    currentPage: 1,
    hasMoreItems: true,
  },
};

export const ClientNotificationStore = signalStore(
  { providedIn: 'root' },
  withState(clientNotificationInitialStore),
  withMethods(
    (
      state,
      clientNotificationService = inject(ClientNotificationService)
    ) => {

      const normalizeNotifications = (notifications) => {
        notifications.forEach(notification => {
          const [message, ...url] = notification.Message.split('.');
          notification.Message = message;
          const reatachUrl = url.join('.')
          notification.Url = reatachUrl.replace(/.*href=(['"])(.*?)\1.*/i, '$2');
        })
        notifications.sort((a, b) => b.OccurredOn.localeCompare(a.OccurredOn));
        return notifications;
      }

      const readAllNotifications = rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('readNotifications') })),
          switchMap(() => clientNotificationService.setReadedAllNotifications()
            .pipe(
              tapResponse({
                next: () => {
                  const notifications = state.notificationsMenu().notifications;
                  notifications.forEach(notification => {
                    if (!notification?.ReadedOn) {
                      notification.ReadedOn = true;
                    }
                  });

                  patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications }, ...setPropLoaded('readNotifications') })
                },
                error: (error) => patchState(state, { ...setPropError('readNotifications', error) })
              })
            )
          )
        )
      )
      return {
        readAllNotifications,
        toggleNotificationsMenu: (open?: boolean) => {
          const unReadedNotifications = state.notificationsMenu().notifications.filter((notification) => !notification.ReadedOn);

          if (state.notificationsMenu().opened && unReadedNotifications?.length) {
            readAllNotifications();
          }
          patchState(state, { notificationsMenu: { ...state.notificationsMenu(), opened: open ?? !state.notificationsMenu().opened } });
        },
        removeNotification: (notificationId: string) => {
          const notifications = state.notificationsMenu().notifications.filter((notification) => notification.NotificationId !== notificationId);
          patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications } });
        },
        listen: rxMethod(
          pipe(
            switchMap((eventTypes: EClientNotificationsEventTypes[]) =>
              clientNotificationService.openConnection(eventTypes).pipe(
                tapResponse({
                  next: (notifications) => {
                    let menuNotifications = state.notificationsMenu().notifications;
                    if (
                      notifications.eventName === EClientNotificationsEventTypes.onCentralNotificationContract ||
                      notifications.eventName === EClientNotificationsEventTypes.onGympassCheckinNotification ||
                      notifications.eventName === EClientNotificationsEventTypes.onTotalpassCheckinNotification) {
                      notifications.eventData.ReadedOn = false;
                      notifications.eventData.isNew = true;
                      menuNotifications.unshift(notifications.eventData);
                      menuNotifications = normalizeNotifications(menuNotifications);
                    }

                    patchState(state, {
                      notifications,
                      notificationsMenu: { ...state.notificationsMenu(), notifications: menuNotifications },
                      ...setPropLoaded('listen'),
                    })
                  },
                  error: (error) =>
                    patchState(state, { ...setPropError('listen', error) }),
                })
              )
            )
          )
        ),
        loadNotifications: rxMethod<void>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('loadNotifications') })),
            switchMap(() => clientNotificationService.getNotifications(1, 14, true)
              .pipe(
                tapResponse({
                  next: (data) => {
                    const notifications = normalizeNotifications(data);
                    patchState(state, {
                      notificationsMenu: {
                        ...state.notificationsMenu(),
                        notifications,
                        currentPage: 1,
                        hasMoreItems: data.length === 14
                      },
                      ...setPropLoaded('loadNotifications')
                    })
                  },
                  error: (error) => patchState(state, { ...setPropError('loadNotifications', error) })
                })
              )
            )
          )
        ),
        loadMoreNotifications: rxMethod<void>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('loadMoreNotifications') })),
            switchMap(() => {
              const currentNotifications = state.notificationsMenu();
              const nextPage = currentNotifications.currentPage + 1;

              return clientNotificationService.getNotifications(nextPage, 14, true);
            }),
            tapResponse({
              next: (data: INotification[]) => {
                const currentItems = state.notificationsMenu().notifications;
                const newNotifications = normalizeNotifications(data);

                patchState(state, {
                  notificationsMenu: {
                    ...state.notificationsMenu(),
                    notifications: currentItems.concat(newNotifications),
                    currentPage: state.notificationsMenu().currentPage + 1,
                    hasMoreItems: data.length === 14
                  },
                  ...setPropLoaded('loadMoreNotifications')
                });
              },
              error: (error) => patchState(state, { ...setPropError('loadMoreNotifications', error) })
            })
          )
        ),
        deleteNotification: rxMethod<string>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('removeNotification') })),
            switchMap((notificationId) =>
              clientNotificationService.deleteNotification(notificationId).pipe(
                tapResponse({
                  next: () => {
                    const notifications = state.notificationsMenu().notifications.filter((notification) => notification.NotificationId !== notificationId);
                    patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications }, ...setPropLoaded('removeNotification') })
                  },
                  error: (error) => patchState(state, { ...setPropError('removeNotification', error) })
                })
              )
            )
          )
        ),
        subscribePushNotifications: rxMethod(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('subscribePushNotifications') })),
            switchMap(() => clientNotificationService.openConnection(['onCentralNotification'])
              .pipe(
                tapResponse({
                  next: (notifications) =>
                    patchState(state, {
                      notificationsMenu: { ...state.notificationsMenu(), notifications },
                      ...setPropLoaded('subscribePushNotifications')
                    }),
                  error: (error) => patchState(state, { ...setPropError('subscribePushNotifications', error) })
                })
              )
            )
          )
        ),

        setPropsBlankState: (props: string[]) => {
          patchState(state, { ...setPropInit(...props) })
        },
      }
    }
  ),
  withReqState('loadNotifications', 'listen', 'readNotifications', 'removeNotification', 'loadMoreNotifications'),
  withHooks({
    onInit: (state) => {
      state.loadNotifications();
    }
  }),
);
